import './playFrame.css';
import '../../../renderer/renderer.css';
import { useContext, useEffect, useRef, useState } from 'react';
import { renderer } from '../../../renderer/Render';
import { Player } from "@lottiefiles/react-lottie-player";
import { KeyBoardEvents } from "@mathbuddy/adaptive-practice-events";
import { AppContext, renderedContext, AdaptivePracticeContext, AdaptiveChallengeContext } from "../../../context/context";
import AdaptiveSound from '../sound/AdaptiveSound';
import { PlayPopup } from './popUp/PlayPopup';
import { AssessmentQuestionStatus as QuestionStatus, IAdaptivePracticeContext } from '../interface/interface';
import { FACheck, FAClockRotateLeft, FADelete, FAExclamationCircle, FAExpand, FARefresh, FARocket, FAShare } from '../../../components/atoms/fontawesomeIcon/Fontawesome';
import { toggleFullSceen } from '../../../utils/utils';
import { Loader } from '../Loader';
import { QuestionNavFooter } from './QuestionNavFooter';
import { themeAnimation, themeImage } from '../theme';

export function PlayFrame() {

  useEffect(() => {
    // A read-only frame is a picture of a question, and a page can hold several of them. Answer
    // keys are handled at the window, so without this one keypress would be answered by every
    // frame on the page. (The full-screen preview is read-only too and loses nothing by it.)
    if (readOnlyFrame) return;
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('mousedown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('mousedown', handleKeyDown);
    };
  }, [])

  const handleKeyDown = (e: any) => {
    const keys = new KeyBoardEvents();
    /**
     * A key typed on a REAL keyboard is the engine's to deal with, and it deals with it in the DOM:
     * every key but backspace, delete and the arrows is preventDefault()ed and written straight into
     * the input box. React's onChange never fires for those, so the input part cannot say the student
     * has entered anything - which left Save grey after a typed number and lit after a backspace, the
     * one key the engine lets the browser handle.
     *
     * So the key is counted here, where it arrives, on the SAME TERMS the engine takes it: only where
     * the question has boxes to type in, and only for a key it would actually enter - its own list,
     * read from the same object that is about to use it - plus the two that delete. A question with
     * nothing to type in is untouched by any amount of typing, and arrows, Tab and Enter move about
     * rather than enter anything.
     */
    const typedInto = document.getElementsByClassName(keys.ev?.clickedVal).length > 0;
    const entersSomething = keys.inputValues?.indexOf(e?.key) >= 0 || e?.key === 'Backspace' || e?.key === 'Delete';
    if (typedInto && entersSomething)
      markTouched();
    keys.handleKeyDown(e);
  }

  const AppState: any = useContext(AppContext);
  const APContext: IAdaptivePracticeContext = useContext(AdaptivePracticeContext);
  /** A frame that only shows a question: no answering, no menus worth drawing, and safe to mount
   *  more than once on a page. */
  const readOnlyFrame = APContext.options?.readOnly === true;
  const showSideMenus = APContext.options?.isShowSideMenus !== false;
  const CHContext: any = useContext(AdaptiveChallengeContext);
  const component = APContext.engineObj.component;
  const userResponceRef = useRef(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const timer = APContext.options?.questionSummaryHeader?.timer;

  useEffect(() => {
    let componentlen = Object.keys(component).length;
    // console.log(componentlen)

    if (timeoutRef.current)
      clearTimeout(timeoutRef.current);

    if (componentlen > 0) {
      userResponceRef.current = false;
      scrollUpToEndOfTheQuestion();

      timeoutRef.current = setTimeout(() => {
        if (!userResponceRef.current) {
          playMusic('backgroundSound');
        }
      }, 1000 * 20)
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [APContext.engineObj.sound])

  useEffect(() => {
    if (!APContext.isCloseFromClick && !APContext.isShowStartPopup && APContext.isShowAdaptivePopup)
      playMusic('successSound');
  }, [APContext.isShowAdaptivePopup])

  const [playSound, setPlaySound] = useState({ isPlay: false, type: '' });
  const [soundConfig, setSoundConfig] = useState(true);

  const updateSoundConfig = () => {
    pauseMusic();
    let configStatus = !soundConfig;
    setSoundConfig(configStatus);
  }

  const playMusic = (type: string) => {
    if (APContext.options?.isMusicAllowed === false) return;
    if (soundConfig == true || type === 'coinSound') {
      setPlaySound({ isPlay: true, type: type + "_" });
      /* The input boxes used to be copied out and written back around this, because a sound starting
         re-rendered the question and React put its own empty string back over what the student had
         typed. The box is no longer React's to overwrite - see Input - so the sound is just a sound
         again, and anything else that sets state here needs nothing either. */
      if (type === 'coinSound')
        setTimeout(() => { setPlaySound({ isPlay: false, type: "" }); }, 500);
    }
  }

  const pauseMusic = () => {
    let playSound = { isPlay: false, type: '' }
    setPlaySound(playSound);
  }

  const speak = () => {
    // NOTHING TO READ IS NOT SOMETHING TO READ.
    //
    // `text` is a string property: assigning undefined to it stores the word "undefined", and the
    // browser duly says it out loud. A question that could not be drawn has no spoken text, so the
    // button used to announce "undefined" instead of staying quiet.
    const text = APContext.engineObj?.sound;
    const toRead = Array.isArray(text) ? text.join(' ') : text;
    if (toRead === undefined || toRead === null || String(toRead).trim() === '') return false;

    let msg = new SpeechSynthesisUtterance();
    let voiceId = 1;
    msg.voice = speechSynthesis.getVoices()[voiceId];
    msg.text = String(toRead);
    msg.volume = 0.5;
    speechSynthesis.speak(msg);
    return false;
  }

  const scrollUpToEndOfTheQuestion = () => {
    // A frame that only shows a question does not scroll itself, and a page can hold several of
    // them - getElementById would find the first one's anchor, not this one's.
    if (readOnlyFrame) return;
    const elementStart = document.getElementById('scrollUpToStartOfTheQuestion');
    if (elementStart)
      elementStart.scrollIntoView({ behavior: 'smooth' });
    // console.log("Scrool Start") 
    setTimeout(() => {
      const elementEnd = document.getElementById('scrollUpToEndOfTheQuestion');
      if (elementEnd)
        elementEnd.scrollIntoView({ behavior: 'smooth' });
      // console.log("Scrool End") 
    }, 2000)
  }

  const saveQuestionResponse = async (answerObj: { givenAnswer: any, actualAnswer: any, isCorrectAns: Boolean }, clickCount: number) => {
    // console.log('saveQuestionResponse');
    userResponceRef.current = true;
    pauseMusic();
    let isCorrectAns = answerObj.isCorrectAns;
    if (isCorrectAns)
      playMusic('coinSound');
    if (!isCorrectAns)
      (!CHContext.isChallenge && clickCount === 1) ? playMusic('hintSound') : playMusic('expSound');

    saveSnapShot();

    if (APContext.saveQuestionResponse)
      await APContext.saveQuestionResponse(answerObj, clickCount);
  }

  const saveSnapShot = () => {
    if (APContext.options?.snapShot?.saveSnapShot) {
      let questionContainer = document.getElementById('question-content');
      if (questionContainer) {
        const clonedContainer = questionContainer.cloneNode(true) as HTMLElement;
        clonedContainer.querySelectorAll('input').forEach((input, index) => { input.setAttribute('value', input.value); input.setAttribute('readonly', 'true'); });
        APContext.options?.snapShot?.saveSnapShot(clonedContainer.innerHTML);
      }
    }
  }

  const onFinish = () => {
    userResponceRef.current = true;
    console.log('onFinish');
    APContext.setShowAdaptivePopup();
    playMusic('successSound');
  }

  const loadContent = () => {
    userResponceRef.current = true;
    pauseMusic();

    if (APContext.loadContent)
      APContext.loadContent();
  }

  /* Keep the question being asked - or read back - in view in the strip. Either host's position, now
     that both draw the same strip: a review of twenty questions scrolls as surely as a sitting does,
     and before this its number could be off the end of the strip with nothing to say where it was. */
  const currentPos = APContext.options?.questionSummaryHeader?.current;
  useEffect(() => {
    if (currentPos !== undefined) {
      const elementQuestionBtn = document.getElementById('question-btn-' + currentPos);
      if (elementQuestionBtn)
        elementQuestionBtn.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
    }
  }, [currentPos])

  /**
   * The ring around a question's number: how that question stands.
   *
   * Green for an answer given - and for a right one, which is the same green the Answered tally
   * uses. Amber for a question with nothing in it, whether it is still to be answered or was left
   * blank: that is something not done, not something wrong, which is why red is kept for a wrong
   * answer alone. Nothing to say about it, nothing drawn, and the number keeps its plain border.
   */
  const getBorderForQuestionStatus = (status?: QuestionStatus) => {
    return (status === QuestionStatus.answered || status === QuestionStatus.correct) ? "var(--mark-correct)"
      : (status === QuestionStatus.wrong) ? "var(--mark-wrong)"
        : (status === undefined) ? undefined
          : "var(--mark-blank)";
  }
  /**
   * WHAT THE QUESTION ON SCREEN IS HOLDING, ready to be handed in: the part that drew the answer
   * puts a way of reading it here as the student works (see registerCommit on the rendered context),
   * and the footer below calls it when they say so.
   *
   * One question, one way in. A question is answered EITHER by its own Done button or by picking one
   * of a set of choices - the engine builds one or the other from the question's input type, never
   * both at once - so there is nothing to choose between here.
   */
  const commitRef = useRef<(() => void) | null>(null);
  const registerCommit = (commit: (() => void) | null) => { commitRef.current = commit; };
  /**
   * HAS THE STUDENT DONE ANYTHING TO THIS QUESTION - picked, typed, dragged, tapped a square.
   *
   * One rule for every kind of question, which is the point. Each kind stores its answer somewhere
   * different, and only some could be read back without the engine: a choice knows what was marked,
   * but a drag or a board or a graph is only readable by pressing Done, and pressing Done on a
   * question nobody touched records an empty answer - the question would come back WRONG rather than
   * unanswered. Asking instead whether they worked in it at all is answerable by every part, because
   * every part that takes an entry goes through this renderer.
   *
   * So: touched and they move on - what is in the question is handed in. Never touched - it stays
   * unanswered, exactly as Skip leaves it, whatever kind of question it is.
   */
  /**
   * Kept in a ref, and announced to the Save button alone.
   *
   * NOT state on this component. Working in a question would then re-render the question itself, and
   * the input box is a controlled React element whose value the ENGINE writes into the DOM directly
   * (its keypad does `input.value += key`) - so React, re-rendering, put its own empty string back
   * and the digit just typed disappeared. Only the button cares whether anything has been done, so
   * only the button is told.
   */
  const touchedRef = useRef(false);
  const tellSaveButton = useRef<((touched: boolean) => void) | null>(null);
  const setTouched = (touched: boolean) => {
    if (touchedRef.current === touched) return;
    touchedRef.current = touched;
    tellSaveButton.current?.(touched);
  };
  /* Said by every part the student can work in, which during a drag is many times a second - hence
     the test above, so all but the first say nothing. */
  const markTouched = () => { setTouched(true); };
  /**
   * A different question on screen: nothing has been done to THIS one yet.
   *
   * Keyed on which question is being shown, not on the drawn question's identity. The engine's own
   * events re-render the screen that hosts this frame, which made a new object each time and reset
   * this a moment after a key press set it - so a digit left Save grey while Clear, which re-renders
   * nothing, left it lit.
   */
  useEffect(() => { setTouched(false); }, [currentPos]);

  /**
   * A QUESTION ALREADY ANSWERED IS A PICTURE OF ITSELF, and a picture does not answer back.
   *
   * It comes back as the student's own working, saved - so it looks exactly like the question they
   * filled in, and the first thing they do is try to change it. Nothing happens, because there is
   * nothing live to press. So it is dimmed, to say as much before they try, and a press says it in
   * words and points at the one thing that WILL work.
   */
  const [snapshotNote, setSnapshotNote] = useState(false);
  const snapshotNoteTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sayItIsSaved = () => {
    setSnapshotNote(true);
    if (snapshotNoteTimer.current) clearTimeout(snapshotNoteTimer.current);
    snapshotNoteTimer.current = setTimeout(() => setSnapshotNote(false), 4000);
  };
  useEffect(() => () => { if (snapshotNoteTimer.current) clearTimeout(snapshotNoteTimer.current); }, []);
  /* Gone the moment it stops being true. It answers a press on THIS question's saved answer, so a
     move to another question - or a Clear, which puts the live question back - takes it away at once
     rather than leaving it to its own few seconds, hanging over a question it says nothing about. */
  useEffect(() => {
    setSnapshotNote(false);
    if (snapshotNoteTimer.current) clearTimeout(snapshotNoteTimer.current);
  }, [currentPos, APContext.options?.snapShot?.innerHTML]);

  /** Save what the question is holding. Nothing done to it, nothing to save. */
  const commitAnswer = () => {
    if (!touchedRef.current) return;
    commitRef.current?.();
    // In, and nothing has been done to it since - which is what greys Save until they change
    // something.
    setTouched(false);
  };
  /** Being ANSWERED rather than read back: there is something to hand in. What the screen says of
   *  itself, never worked out from the questions - see onSubmit on the options. It decides the row
   *  under the frame, the tallies, and the words for a question with no answer in it. */
  const isSitting = APContext.options?.onSubmit !== undefined;
  /** And of those, the one that holds the answer back until the student saves it - see
   *  saveAnswersByHand on the options. The assessment does not: it answers as it always has. */
  const saveByHand = APContext.options?.saveAnswersByHand === true;
  const summary = APContext.options?.questionSummaryHeader;
  const questionStatus = summary?.questionsStatus;
  /**
   * THE QUESTIONS, as one strip above the question - the only place this frame draws them, whether
   * the sitting is being played or read back afterwards. Two designs for the one thing only left
   * the reader working out which was which.
   *
   * What differs is what a number SAYS and whether it can be reached, so that is all this works
   * out: playing, the ring is the question's status and one not yet reached cannot be jumped to;
   * reading back, the ring is the mark it was given and every question is open. The strip itself,
   * and everything below, is the same either way.
   *
   * Counted by the statuses where there are statuses: a screen may know of more questions than it
   * is showing, and the numbers are of the ones on screen.
   */
  const numbers = summary === undefined ? [] :
    Array.from({ length: questionStatus?.length ?? summary.total }, (_, i) => {
      const status = questionStatus?.[i];
      const isCurrent = i === summary.current;
      const notReached = status === QuestionStatus.notVisited;
      return {
        current: isCurrent,
        border: getBorderForQuestionStatus(status),
        faded: notReached,
        /* What the ring means, in words, for whoever cannot tell one ring from another - the same
           hover the frame's buttons have, which is the browser's own.
           About the ANSWER, never about the reader: a teacher opens this same review to read a
           student's attempt, so "you got this right" would be describing the wrong person. */
        label: `Question ${i + 1}, ` + (
          status === QuestionStatus.answered ? 'answered'
            : status === QuestionStatus.correct ? 'answered correctly'
              : status === QuestionStatus.wrong ? 'answered incorrectly'
                : notReached ? 'not opened yet'
                  : status === QuestionStatus.unanswered ? (isSitting ? 'not answered yet' : 'not attempted')
                    : isCurrent ? 'the one on screen'
                      : 'open it'),
        onPick: notReached ? undefined : () => { summary.setCurrentPos(i) }
      };
    });
  /** What the question on screen is worth, where the host says to show it. It is drawn in the coin
   *  counter's place - see below - because that is what it becomes when the answer is right. */
  const questionPoints = APContext.options?.questionPoints;

  return (<>
    {(AppState.windowWidth < AppState.windowHeight) && (
      <div className="adaptivePortraitContainer d-flex flex-column justify-content-center align-items-center">
        <h4>Math Buddy works only in landscape mode</h4>
        <span className='text'> Please rotate your device! <br></br>
          (If you are not able to rotate, check your mobile settings and ensure "Auto-rotate screen" is switched on)
        </span>
      </div>
    )}
    {(AppState.windowWidth >= AppState.windowHeight) && (
      <div className="frame-container d-flex flex-column justify-content-around align-items-center" >
        {(!(APContext.options?.isShowClose === false)) && (
          <button data-bs-toggle="tooltip" title='Close' className="icon" onClick={() => { APContext.setShowAdaptivePopup(true) }}><img className="close-skill" src={themeImage('close.svg')}></img></button>
        )}

        {(numbers.length > 0) && (
          <div id="question-summary-header">
            {/* One row, laid out by flex rather than by columns, so its ends sit exactly where the
                question below it starts and finishes: the clock (or, untimed, the numbers) begins at
                the panel's left edge and the tallies finish at its right. Columns could not do that -
                an empty column still took its share, which left the row looking lopsided. */}
            <div className='summary-row' style={{ height: "100%", width: "100%" }}>
              {(timer !== undefined) && (
                <div className="time-left-panel d-flex flex-column align-items-center justify-content-center">
                    <span style={{ fontSize: "1vw" }}><FAClockRotateLeft color='#red' size='xl'></FAClockRotateLeft> Time Left</span>
                  {timer.initialTime > 0 ?
                    <Timer timer={timer} onTimerEnd={timer.onTimerEnd} />
                    : <span style={{ fontSize: "1.5vw" }}> 00:00:00 </span>}
                </div>
              )}

              <div className="summary-numbers d-flex align-items-center justify-content-center">
                <div className='question-number-panel'>
                  {numbers.map((question, i) => (
                    <div key={i}
                      id={'question-btn-' + i}
                      data-bs-toggle="tooltip" title={question.label}
                      className={`queBtn ${question.current ? 'planet-glow-current' : ''} d-flex align-items-center justify-content-center ${"bubble-animate-" + ((i % 6) + 1)}`}
                      style={{
                        borderColor: question.current ? '#fff' : question.border,
                        boxShadow: question.current ? '0 0 15px 5px #fff' : 'none',
                        opacity: question.faded ? 0.8 : undefined
                      }}
                      onClick={question.onPick}>
                      <span>{i + 1}</span>
                    </div>
                  ))}
                </div>
              </div>

              {(isSitting) && (
              <div className='d-flex align-items-center justify-content-center summary-tallies'>
                <div className="answered-panel d-flex flex-column align-items-center justify-content-center">
                  <span style={{ fontSize: "1vw" }}><FARocket color='var(--mark-correct)' size='xl'></FARocket> Answered</span>
                  <span style={{ fontSize: "1.5vw" }}> {summary?.answeredCount ?? (questionStatus || []).filter(status => status === QuestionStatus.answered).length} </span>
                </div>

                <div className="unanswered-panel d-flex flex-column align-items-center justify-content-center">
                  <span style={{ fontSize: "1vw" }}><FAExclamationCircle color='var(--mark-blank)' size='xl'></FAExclamationCircle> Unanswered</span>
                  <span style={{ fontSize: "1.5vw" }}> {summary?.unansweredCount ?? (questionStatus || []).filter(status => status === QuestionStatus.unanswered || status === QuestionStatus.notVisited).length} </span>
                </div>

              </div>
              )}

            </div>
          </div>
        )}

        {/* ALL THE ROOM THE STRIP ABOVE AND THE ROW BELOW DO NOT NEED.
       
            Those two are sized by what is in them - a strip of numbers, a row of buttons - and this
            takes the rest, rather than everything being a share of the screen that had to be kept in
            step by hand. The question is what a student is here for, so it gets what is left. The
            height below is only the fallback for a frame drawn with no row under it at all. */}
        <div id="frame" className={isSitting || numbers.length > 0 ? 'frame-fills' : ''}
          style={{ height: "100%" }}>
          <div className="row margin_padding_none_strict" style={{ height: '100%' }}>
            {(APContext.notificationMessage !== '') && (
              <div className='notificationMessage slideInFromLeft'>
                {APContext.notificationMessage}
              </div>
            )}

            {showSideMenus && (
            <div id="left-menu" className="col-1 d-flex flex-column align-items-start justify-content-start">
              {(APContext.streaks !== undefined) && (
                <div className="strick-icon-wrapper main-btn bottom-gradient d-flex align-items-center justify-content-center">
                  <a href="#" className="w-100 d-flex align-items-center justify-content-evenly">
                    <div className="img-wrapper" style={{ width: "20%" }}>
                      <img src={themeImage('power-icon.svg')} alt=""></img>
                    </div>
                    <div className="text-wrapper" style={{ minWidth: "50%" }}>
                      <span>{APContext.streaks}</span>
                    </div>
                  </a>
                </div>
              )}

              {(APContext.options?.isMusicAllowed !== false) && (
                <div data-bs-toggle="tooltip" title={!soundConfig ? 'Play music' : 'Stop music'}
                  className="music-icon-wrapper main-btn-small bottom-gradient d-flex align-items-center justify-content-center" onClick={updateSoundConfig}>
                  {(playSound.isPlay) && (<AdaptiveSound type={playSound.type}></AdaptiveSound>)}
                  <a href="#">
                    <div className="img-wrapper">
                      <img src={themeImage(`music-icon${soundConfig ? '-on' : ''}.svg`)} alt=""></img>
                    </div>
                  </a>
                </div>
              )}
              <div data-bs-toggle="tooltip" title='Read question'
                className="user-icon-wrapper main-btn-small bottom-gradient d-flex align-items-center justify-content-center" onClick={speak}>
                <a href="#">
                  <div className="img-wrapper">
                    <img src={themeImage('user-icon.svg')} alt=""></img>
                  </div>
                </a>
              </div>

              {(APContext.options?.isToggleFullSceren !== false) && (
                <div data-bs-toggle="tooltip" title='Full screen'
                  className="user-icon-wrapper main-btn-small bottom-gradient d-flex align-items-center justify-content-center" onClick={() => { toggleFullSceen('fullScreen') }}>
                  <a href="#">
                    <div className="img-wrapper">
                      <FAExpand color='#fff' size='xl'></FAExpand>
                    </div>
                  </a>
                </div>
              )}

              {/* What they were told before they started, for a student who wants it again. The
                  screen that has instructions offers this; one that has none draws no button. */}
              {(APContext.options?.onShowInstructions) && (
                <div data-bs-toggle="tooltip" title='Read the instructions again'
                  className="user-icon-wrapper main-btn-small bottom-gradient d-flex align-items-center justify-content-center"
                  onClick={APContext.options?.onShowInstructions}>
                  <div className='reference-btn'>
                    i
                  </div>
                </div>
              )}

              {/* The Clear a screen keeps HERE: one that saves by hand has it in the row under the
                  frame instead, where Save stands on a question still being answered, and two of
                  them would be two answers to the same question. */}
              {(APContext.options?.snapShot?.clearSnapShot && !saveByHand) && (
                <div data-bs-toggle="tooltip" title='Clear Snapshot'
                  className="user-icon-wrapper main-btn-small bottom-gradient d-flex align-items-center justify-content-center"
                  onClick={APContext.options?.snapShot?.clearSnapShot}>
                  <a href="#">
                    <div className="img-wrapper" style={{ color: '#fff' }}>
                      <FADelete color='#fff' size='xl'></FADelete>
                      clear
                    </div>
                  </a>
                </div>
              )}

              {(APContext.options?.isShowReference) && (
                <div data-bs-toggle="tooltip" title='Reference Material'
                  className="user-icon-wrapper main-btn-small bottom-gradient d-flex align-items-center justify-content-center" onClick={() => APContext.options?.showReferenceScreen!(true)}>
                  <div className='reference-btn'>
                    R
                  </div>
                </div>
              )}

              {/* LAST in the panel, under Full screen and whatever else the screen is given: it is
                  the one control here that changes the question rather than the way it is shown, and
                  it belongs to the panel's own buttons rather than to the footer, where the
                  explanation box opened over it. Dressed as its neighbours are, so it is as visible
                  as they are in every theme. */}
              {(APContext.options?.isShowReload) && (
                <div data-bs-toggle="tooltip" title='Refresh question'
                  className="user-icon-wrapper main-btn-small bottom-gradient d-flex align-items-center justify-content-center" onClick={APContext.loadContent}>
                  <div className="img-wrapper">
                    <FARefresh color='#fff' size='xl'></FARefresh>
                  </div>
                </div>
              )}


              {(APContext.options?.session?.isLive) && (
                <div className="footer d-flex align-items-start justify-content-evenly">
                  <button id="shareBtn" data-bs-toggle="tooltip" title="Show to students"
                    className="d-flex flex-column align-items-center justify-content-center"
                    onClick={APContext.options.session.onShare}
                    disabled={false}>
                    <FAShare color='#370A52'></FAShare>
                  </button>

                  <button id="checkBtn" data-bs-toggle="tooltip" title={
                    APContext.options.session.status === 'STARTED' ? 'Started' :
                      APContext.options.session.status === 'COMPLETED' ? 'Completed' : 'Not started'}
                    className="d-flex flex-column align-items-center justify-content-center"
                    onClick={APContext.options.session.onComplete}
                    disabled={APContext.options.session.status !== "STARTED"}>
                    <FACheck color={APContext.options.session.status === "COMPLETED" ? "green" : '#370A52'}></FACheck>
                  </button>
                </div>
              )}

            </div>
            )}
            <renderedContext.Provider value={{
              explanation: component.explanation,
              hint: component.hint, challenge: {},
              isSafeZone: APContext.isSafeZone,
              isLastQuestion: APContext.isLastQuestion,
              isShowExpNext: !(APContext.options?.isShowExpNext === false),
              isSingleAttempt: APContext.options?.singleAttempt === true,
              showFinishOnLast: APContext.options?.showFinishOnLast === true,
              reviewMode: APContext.reviewMode,
              type: 'adaptive-practice',
              // Shown, not played: the parts read this and wire no handlers at all.
              readOnly: readOnlyFrame,
              // The worked solution, opened for whoever built the question rather than answered it.
              // Passed through RAW, not coerced: undefined has to stay undefined, because it is a
              // third state and not a "no" - see showAnswer on the options for what each one means.
              // Coercing it here made every mode that never sets it look like an explicit false.
              showAnswer: APContext.options?.showAnswer,
              saveQuestionResponse: saveQuestionResponse,
              /* HANDED IN RATHER THAN ANSWERED QUESTION BY QUESTION - an assessment or a quiz, which
                 this frame knows by having been given a Submit. Only there does it offer the two
                 handles below, and their being here is the whole signal: a part that is given a way
                 to register an answer knows the answer is being held for the Save button, and a part
                 that is not keeps the behaviour it has always had, where answering IS the answer.
                 No flag to read beside them, and nothing to keep in step - the frame grants the
                 capability or it does not, and it can grant them one at a time later if a screen
                 ever wants only one of the two.
                 Not singleAttempt, which was the first guess: that says how many tries a question
                 gets, and a mode can want one try while answering still counts as it happens. */
              ...(saveByHand ? { registerCommit, markTouched } : {}),
              onFinish: onFinish,
              loadContent: loadContent
            }}>
              {(APContext.options?.snapShot?.innerHTML) ? (
                <div id="question-content"
                  // style={{ border: "3px solid orange", borderRadius: "10px" }}
                  className={`col-10 d-flex flex-column${saveByHand ? ' question-snapshot' : ''}`}
                  contentEditable='false'
                  /* On the press, not the click: a student who tries to DRAG a piece of a saved
                     question never completes a click, and on a phone there is no click to wait for.
                     A pointer press covers mouse, touch and pen alike. */
                  onPointerDown={saveByHand ? sayItIsSaved : undefined}
                  dangerouslySetInnerHTML={{ __html: APContext.options?.snapShot?.innerHTML }}></div>
              ) : (<div id="question-content" className="col-10 d-flex flex-column">
                {(component.heading) && (renderer(component.heading))}
                <div id="question-content-inner" className='w-100 d-flex flex-column align-items-center justify-content-start'>
                  <div id="scrollUpToStartOfTheQuestion"></div>
                  {
                    Object.keys(component).map((val) => {
                      if (val === 'heading')
                        return;
                      //debugger; 
                      let objRendered;
                      objRendered = renderer(component[val]);
                      //console.log(objRendered);
                      return objRendered;
                    }

                    )
                  }
                  <div id="scrollUpToEndOfTheQuestion"></div>
                </div>
              </div>)}
            </renderedContext.Provider>
            {showSideMenus && (
            <div id="right-menu" className="col-1 d-flex flex-column align-items-end justify-content-start">
              {(APContext.coins !== undefined || questionPoints !== undefined) && (
                <div className="coin-icon-wrapper main-btn bottom-gradient d-flex align-items-center justify-content-center"
                  data-bs-toggle="tooltip" title={APContext.coins !== undefined ? 'Coins' : 'What this question is worth'}>
                  <a href="#" className="w-100 d-flex align-items-center justify-content-evenly">
                    <div className="img-wrapper" style={{ width: "20%", position: "relative" }}>
                      <img src={themeImage('coin-icon.svg')} alt=""></img>
                      {(APContext.isCoinAnimation) && (
                        <Player id={"coinAnimation"} style={{ "width": "100px", "height": "100px" }} className={"coin-animation"} autoplay speed={1.5} src={themeAnimation('coin.json')}></Player>
                      )}
                    </div>
                    <div className="text-wrapper" style={{ minWidth: "50%" }}>
                      {/* Coins while playing for them; otherwise what the question in front of them is
                          worth - which IS coins, for a correct answer - so it belongs in the same box,
                          in the same white the rest of the frame uses. */}
                      <span>{APContext.coins !== undefined ? APContext.coins : questionPoints}</span>
                    </div>
                  </a>
                </div>
              )}

              {(APContext.coins === undefined && questionPoints === undefined) && (
                /* Holds the coin button's place when a mode hides it. HOTS reports a sitting's
                   coins in the host at the end rather than in the frame, so the counter is
                   absent - which made the progress bar this rail's first child and let
                   justify-content-start pull it to the top. This keeps the rail's geometry
                   identical to adaptive practice at every breakpoint, because it IS the same
                   box - the counter's own class list, so any rule that sizes it sizes this too,
                   whatever the media query makes them. visibility:hidden rather than a computed
                   margin, so there is no number here to keep in sync with the CSS. */
                <div className="coin-icon-wrapper main-btn bottom-gradient d-flex align-items-center justify-content-center"
                  style={{ visibility: 'hidden' }} aria-hidden="true"></div>
              )}

              {(APContext.isSafeZone) && (
                <div className='safeZoneText slideInFromLeft'> Safe Zone </div>
              )}

              {(APContext.meter.level !== '') && (
                <div className="progress-wrapper-2">
                  <span style={{ bottom: (APContext.meter.percentage < 10 ? 0 : APContext.meter.percentage - 10) + "%" }}>{APContext.meter.level}</span>
                  <div className="progress-wrapper">
                    <progress id="progressBar" max="100" value={APContext.meter.percentage}></progress>
                  </div>
                </div>
              )}

            </div>
            )}
          </div>

          {/* OVER the frame, not inside its grid row. A note in the row is a grid cell, and the
              renderer's own `.row > *` strips every cell's padding with an !important - which is why
              this box sat cut to its words however much padding it was given. */}
          {(snapshotNote) && (
            <div className="snapshot-note">Press the Clear button if you want to change your answer</div>
          )}

        </div>

        {/* What to do with the question on screen - walking them, keeping an answer, handing the
            quiz in. Drawn only where there is something to hand in, which is what a Submit means;
            the row itself, and every word in it, lives in QuestionNavFooter. */}
        {(isSitting) && (
          <QuestionNavFooter onSave={commitAnswer} bindTouched={tellSaveButton}
            touchedAlready={touchedRef.current} saveByHand={saveByHand}></QuestionNavFooter>
        )}

        {(!APContext.isLoader && (APContext.isShowAdaptivePopup || APContext.isShowStartPopup)) && (
          <PlayPopup
            info={APContext.getPopUpInfo()}
            onContinue={APContext.setShowAdaptivePopup}
            onStop={APContext.onclose}
            onStart={() => { if (APContext.setShowStartPopup !== undefined) { APContext.setShowStartPopup(false) } }}
          />
        )}

        {(APContext.isLoader) && (
          <Loader />
        )}

      </div >
    )
    }
  </>)

}


function Timer({ timer, onTimerEnd }: { timer: any, onTimerEnd: () => void }) {
  const timeLeftRef = useRef(timer?.initialTime || -1);
  const [timeLeft, setTimeLeft] = useState(timeLeftRef.current);

  /**
   * The host may know better, and only ever downwards.
   *
   * This counts on its own so the number never jumps about, but a tab in the background has its
   * timers throttled and the count silently stalls. A host that hears a smaller figure from the
   * server hands it over here, and it is adopted; a larger one is ignored, so nobody gains time by
   * hiding the tab. A host that never revises the figure - the assessment - never reaches this.
   */
  useEffect(() => {
    const revised = timer?.initialTime;
    // Strictly ABOVE zero. A host that hands over 0 is saying the sitting is over - the assessment
    // does exactly that as it submits - and adopting it here would let the tick below land on zero
    // and call `onTimerEnd`, submitting a second time. Reaching zero is the countdown's own job.
    if (typeof revised !== 'number' || revised <= 0) return;
    if (timeLeftRef.current > 0 && revised < timeLeftRef.current) {
      timeLeftRef.current = revised;
      setTimeLeft(revised);
    }
  }, [timer?.initialTime]);


  useEffect(() => {
    if (timer.isStarted === true && timeLeftRef.current !== 0 // to prevent re-initialization after timer ends
    ) {
      const timer = setInterval(() => {
        timeLeftRef.current = Math.max(timeLeftRef.current - 1, 0);
        setTimeLeft(timeLeftRef.current);
        if (timeLeftRef.current === 0) {
          onTimerEnd();
          clearInterval(timer);
        }
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [timer?.isStarted]);

  const formatTime = (seconds: number) => {
    seconds = Math.max(seconds, 0);
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  return (<span style={{ fontSize: "1.5vw" }}> {formatTime(timeLeft)} </span>)
}